import React, { useState } from 'react';
import { fillingStatusFromValue } from 'components/utils/fillingType';
import useChangeFillingStatus from './useChangeFillingStatus';
import FillingMeasurementsDialog from '../FillingMeasurementsDialog';

// Filling change shared by the filling toggle and the filling dialog. When the new
// status makes the calculation ignore volume measurements (saved or entered), the
// operator chooses to cancel, clear them (saved row + form) or keep them. Without a
// form (weighed containers), the change is applied directly.
export default function useFillingChange({
  inventoryData, itemData, setInventoryData, formik,
}) {
  const {
    changeFillingStatus, measurementsToResolve, currentRawStatus, loading,
  } = useChangeFillingStatus({ inventoryData, itemData, setInventoryData });
  const [request, setRequest] = useState(null);

  // The same measurement list is cleared in the saved row and in the form. Nothing is
  // cleared or closed unless the change was committed locally: on failure the entries
  // and the question stay, so the operator can retry.
  const apply = async (newStatus, measurements, onDone) => {
    const keys = measurements.map(({ key }) => key);
    const saved = await changeFillingStatus(newStatus, { clearKeys: keys });
    if (!saved) return;
    if (formik) {
      // The form keeps hidden values: clear them so a later submit cannot write them back.
      keys.forEach((key) => {
        if (formik.values[key]) formik.setFieldValue(`${key}.value`, '', false);
      });
    }
    setRequest(null);
    if (onDone) onDone();
  };

  const requestFillingChange = (newStatus, onDone) => {
    const measurements = formik ? measurementsToResolve(newStatus, formik.values) : [];
    if (!measurements.length) {
      apply(newStatus, [], onDone);
      return;
    }
    setRequest({
      newStatus, measurements, onDone, filling: fillingStatusFromValue(newStatus),
    });
  };

  const dialog = (
    <FillingMeasurementsDialog
      request={request}
      loading={loading}
      onCancel={() => setRequest(null)}
      // Keeping changes the filling only: saved values stay, unsaved entries stay to submit.
      onKeep={() => apply(request.newStatus, [], request.onDone)}
      onClear={() => apply(request.newStatus, request.measurements, request.onDone)}
    />
  );

  return {
    requestFillingChange, currentRawStatus, loading, dialog,
  };
}
